/**
 * components/IconButton
 * A 26px square button holding one icon — the sidebar's collapse chevron and
 * the drawer / modal close ×. Always carries an accessibility label, because
 * a glyph alone tells a screen reader nothing.
 */
import { useState } from 'react';
import { Pressable, StyleSheet, type ViewStyle } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { isWeb } from '../../lib/responsive';
import Icon, { type IconName } from '../Icon';

export interface IconButtonProps {
  icon: IconName;
  onPress: () => void;
  /** Required — the icon is not a label. */
  accessibilityLabel: string;
  size?: number;
  iconSize?: number;
  /** Off for the drawer close, which sits on a bare surface. */
  bordered?: boolean;
  style?: ViewStyle;
}

export function IconButton({
  icon,
  onPress,
  accessibilityLabel,
  size = 26,
  iconSize = 13,
  bordered = true,
  style,
}: IconButtonProps) {
  const [hovered, setHovered] = useState(false);
  const active = hovered && isWeb;

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.button,
        {
          width: size,
          height: size,
          borderWidth: bordered ? 1 : 0,
          borderColor: active ? neutral.borderStrong : neutral.border,
          backgroundColor: active ? neutral.surface2 : bordered ? neutral.surface : 'transparent',
        },
        style,
      ]}
    >
      <Icon name={icon} size={iconSize} color={neutral.ink2} strokeWidth={1.4} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.control,
    flexShrink: 0,
  },
});

export default IconButton;
